import { useMutation } from "@apollo/client/react";

import logo from 'assets/logotyp.svg'

import { ILoginProps } from 'containers/Login/LoginContainer'
import mutation from 'graphql/mutations/mutationCreateToken'
import * as Sentry from '@sentry/browser'
import { jwtDecode } from 'jwt-decode'
import React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import microsoftLogo from './assets/microsoft.svg'
import bg from './assets/bg.jpeg'

import { getAuth, OAuthProvider, signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth'
import { useForm } from 'react-hook-form'
import {
  createToken as createTokenTypes,
  createTokenVariables,
} from 'graphql/mutations/types/createToken'

const provider = new OAuthProvider('microsoft.com')

const LoginView: React.FC<ILoginProps> = (props) => {
  const { login, SSO, emailLogin } = props
  const [SSOError, setSSOError] = useState<string | null>(null)
  const { t } = useTranslation()
  // Check if sso login began within the last 60 seconds
  const { register, handleSubmit } = useForm<{ email: string; password: string }>()

  const [createToken, { error: e }] = useMutation<createTokenTypes, createTokenVariables>(mutation)
  const [loginError, setLoginError] = useState<string | null>(null)
  const onSubmit = async (data: { email: string; password: string }) => {
    setLoginError(null)
    try {
      const response = await createToken({
        variables: { username: data.email, password: data.password },
      })
      const token = response.data?.createToken
      if (!token) {
        setLoginError('Login failed')
        return
      }
      login(token)
      const username = jwtDecode<{ user?: { username?: string } }>(token)?.user?.username
      if (username) Sentry.setUser({ username })
    } catch (err) {
      // Error is also captured by Apollo's useMutation hook in the 'e' variable
      // This catch handles any unexpected errors
      setLoginError((err as Error)?.message || 'Login failed')
    }
  }

  const loginWithSSO = React.useCallback(async () => {
    try {
      const auth = getAuth()
      const userCred = await signInWithPopup(auth, provider)
      const user = userCred?.user

      if (!user) {
        return
      } else {
        try {
          const id_token = await auth.currentUser?.getIdToken(/* forceRefresh */ true)
          const response = await createToken({ variables: { id_token } })
          const token = response.data?.createToken
          if (!token) {
            setSSOError('SSO login failed')
            return
          }
          login(token)
          const username = jwtDecode<{ user?: { username?: string } }>(token)?.user?.username
          if (username) Sentry.setUser({ username })
        } catch (err) {
          firebaseSignOut(auth)
          setSSOError((err as Error)?.message)
        }
      }
    } catch (err) {
      setSSOError((err as Error)?.message || 'SSO login failed')
    }
  }, [login, createToken])

  return (
    <div className="min-h-screen bg-white flex">
          <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
            <div className="mx-auto w-full max-w-sm lg:w-96">
              <div>
                <img className="h-12 w-auto" src={logo} alt="Workflow" />

                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  {t('LoginView.title')}
                </h2>
              </div>

              <div className="mt-8">
                <div>
                  {emailLogin && (
                    <div className="mt-6">
                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t('general.email')}
                          </label>
                          <div className="mt-1">
                            <input
                              {...register('email', { required: true })}
                              id="email"
                              name="email"
                              type="email"
                              autoComplete="email"
                              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t('general.password')}
                          </label>
                          <div className="mt-1">
                            <input
                              {...register('password', { required: true })}
                              id="password"
                              name="password"
                              type="password"
                              autoComplete="current-password"
                              required
                              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                        <span className="text-red-500">
                          {e?.message?.includes('Credential') || loginError?.includes('Credential')
                            ? t('LoginView.CredentialsWrong')
                            : loginError || null}
                        </span>
                        <div>
                          <button
                            data-testid="email-login-button"
                            type="submit"
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            {t('LoginView.button')}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {SSO && (
                    <div className="mt-6 relative">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        {SSO && emailLogin && (
                          <span className="px-2 bg-white text-gray-500">
                            {t('LoginView.SSOTitle2')}
                          </span>
                        )}
                        {SSO && !emailLogin && (
                          <span className="px-2 bg-white text-gray-500">
                            {t('LoginView.SSOTitle1')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {SSO && (
                  <div>
                    <span className="text-red-500">
                      {SSOError?.includes('domain that is not allowed')
                        ? t('LoginView.NotAllowedDomain')
                        : null}
                    </span>
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div />
                      <div>
                        <div
                          data-testid="login-with-sso"
                          onClick={loginWithSSO}
                          className="cursor-pointer w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-xs bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                        >
                          <span className="sr-only">Sign in with Microsoft</span>
                          <img src={microsoftLogo} alt="Microsoft logo" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      <div className="hidden lg:block relative w-0 flex-1">
        <img className="absolute inset-0 h-full w-full object-cover" src={bg} alt="" />
      </div>
    </div>
  )
}

export default LoginView
